import React, { useState, useEffect, useMemo } from 'react';
import { UserCircle } from '@phosphor-icons/react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { sendBulkFaceEmail } from '../../../../UtilsEmail';

import personImg from '../../../../assets/person.png';
import { EmailSendGroup } from '../../../../components/form';
import { Button, Alert } from '../../../../components/general';

import { URL, FACE_EMAIL_STATUS } from '../../../../constants';

import { useProjectStore } from '../../../../stores/project';

import { Person, PeopleEmailState, SGTemplate } from '../../../../types';
import { isEmpty, getPluralPhrase, getUrl } from '../../../../utils';

import EmailPreviewModal from '../../Settings/EmailHubTab/components/emailPreviewModal';
import MaAccordion from './maAccordion';

interface Props {
  isUploaded: boolean;
  people: Person[];
  emailTemplate: SGTemplate;
}

const MaPhotosBlock: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const { isUploaded, people, emailTemplate } = props;

  const { currentProject } = useProjectStore();

  const [emailState, setEmailState] = useState<PeopleEmailState>({});
  const [isEmailsSending, setIsEmailsSending] = useState(false);

  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);

  const isAnalysisStarted = currentProject?.stage.toLowerCase() !== 'upload';
  const isComplete = isEmpty(people);

  // get people with data
  useEffect(() => {
    if (isEmpty(people)) return;

    const peopleTemp: Record<string, any> = {};

    people.forEach((person: Person) => {
      const id = person.face.elementId;

      peopleTemp[id] = {
        email: person.face.email || '',
        status: FACE_EMAIL_STATUS.NOT_SENT,
        err: null,
      };
    });

    setEmailState(peopleTemp);
  }, [people]);

  const { canBulkShare, sharePopoverData } = useMemo(() => {
    const canBulkShare = people.some(
      (p) => !!emailState[p.face.elementId]?.email,
    );

    const sharePopoverData = {
      header: 'Request from all',
      body: canBulkShare
        ? 'Send email to all with email below and request a photo'
        : 'Unable to request, as no attendee emails are provided. Fill in the emails below, and share either individually or in bulk',
    };

    return { canBulkShare, sharePopoverData };
  }, [people, emailState]);

  if (!currentProject) return;

  const handleBulkSend = async () => {
    setIsEmailsSending(true);

    await sendBulkFaceEmail({
      type: 'missing-photos',
      project: currentProject,
      people,
      emailState,
      setEmailState,
    });

    setIsEmailsSending(false);
  };

  return (
    <React.Fragment>
      <MaAccordion
        icon={<UserCircle size="1.5rem" />}
        heading="Photos"
        subheading={
          !isUploaded ? (
            <>No people uploaded yet</>
          ) : !isComplete ? (
            <>
              <b>{getPluralPhrase(people, 'person')}</b> missing a photo
            </>
          ) : (
            <>All people have a photo</>
          )
        }
        isComplete={isComplete}
        isOpen={!isAnalysisStarted && !isComplete}
        isDisabled={!isUploaded}
      >
        {!isEmpty(people) ? (
          <React.Fragment>
            {isAnalysisStarted && (
              <Alert
                variant="grey"
                alertData={{
                  variant: 'info',
                  text: (
                    <>
                      <span className="font-bold">Note</span>: Analysis has
                      already started. If new photos are uploaded, you will need
                      to go back to{' '}
                      <Link to={getUrl(URL.VIDEO_BUILDER, eventId)}>
                        Video Builder
                      </Link>{' '}
                      and hit 'Analyse' again.
                    </>
                  ),
                }}
              />
            )}

            <div className="w-full flex items-center gap-4">
              <Button
                onClick={handleBulkSend}
                variant="pill"
                colour="black"
                className="w-full sm:w-52"
                isSubmitBtn
                isProcessing={isEmailsSending}
                disabled={!canBulkShare}
                allowPointerEvents
                popover={sharePopoverData}
              >
                Request from All
              </Button>

              <div className="text-sm text-black/[0.5]">
                The attendees will receive{' '}
                <Button
                  onClick={() => setShowEmailPreviewModal(true)}
                  variant="link"
                >
                  Missing Photos
                </Button>{' '}
                email
              </div>
            </div>

            <div className="w-full flex flex-wrap gap-x-12 gap-y-8">
              {people.map((person) => (
                <div className="w-full xl:w-[calc((100%_-_3rem)_/_2)] flex flex-col gap-2 sm:gap-4">
                  <div className="flex items-center gap-4">
                    <img
                      src={personImg}
                      className="w-10 min-w-10 h-10 rounded-full object-cover"
                      alt={`${person.face.name} Icon`}
                    />

                    <h5>{person.face.name}</h5>
                  </div>

                  <EmailSendGroup
                    type="missing-photos"
                    person={person}
                    emailState={emailState}
                    setEmailState={setEmailState}
                    fieldText="request"
                    link={`${getUrl(URL.CHECK_IN, eventId, person.face.elementId, true)}?reqAssets=photo`}
                    components={{
                      emailInput: true,
                      sendBtn: 'rec',
                      copyBtn: 'rec',
                    }}
                  />
                </div>
              ))}
            </div>
          </React.Fragment>
        ) : (
          <Alert
            variant="green"
            alertData={{
              variant: 'info',
              text: 'No worries – everyone already has a photo, so there’s nothing you need to do',
            }}
          />
        )}
      </MaAccordion>

      <EmailPreviewModal
        show={showEmailPreviewModal}
        onHide={() => setShowEmailPreviewModal(false)}
        template={emailTemplate}
      />
    </React.Fragment>
  );
};

export default MaPhotosBlock;
